export type Permission =
  | "dashboard.read"
  | "request.create"
  | "request.procurement.create"
  | "request.read.own"
  | "request.read.department"
  | "request.read.all"
  | "request.manage"
  | "offer.read"
  | "offer.manage"
  | "supplier.read"
  | "supplier.manage"
  | "supplier.identity.manage"
  | "supplier.contact.manage"
  | "supplier.document.read"
  | "supplier.document.verify"
  | "supplier.document.manage"
  | "supplier.qualification.read"
  | "supplier.qualification.evaluate"
  | "supplier.qualification.decide"
  | "supplier.qualification.suspend"
  | "supplier.audit.read"
  | "rfq.read"
  | "rfq.manage"
  | "rfq.publish"
  | "rfq.audit.read"
  | "rfq.invitation.manage"
  | "rfq.deadline.override"
  | "rfq.sealed.open"
  | "supplier_portal.question.manage"
  | "contract.read"
  | "contract.manage"
  | "contract.review"
  | "contract.approve"
  | "contract.audit.read"
  | "contract.compliance.evaluate"
  | "contract.order.authorize"
  | "purchase_order.read"
  | "purchase_order.create"
  | "purchase_order.manage"
  | "purchase_order.receive"
  | "purchase_order.quality.review"
  | "purchase_order.cancel"
  | "approval.read.assigned"
  | "approval.decide.assigned"
  | "report.read"
  | "dof.read"
  | "dof.read.all"
  | "dof.create"
  | "dof.evaluate"
  | "dof.complete.action"
  | "dof.verify.effectiveness"
  | "production.entry.read"
  | "production.plan.read"
  | "production.plan.write"
  | "production.plan.activate"
  | "production.entry.write"
  | "production.run.execute"
  | "production.run.manage"
  | "production.report.read"
  | "production.master.manage"
  | "production.assignment.manage"
  | "production.terminal.manage"
  | "production.technical.manage"
  | "material.preparation.read"
  | "material.preparation.count.main"
  | "material.preparation.count.staging"
  | "material.preparation.workflow"
  | "material.preparation.override"
  | "label.read"
  | "admin.users.manage"
  | "admin.departments.manage"
  | "admin.workflows.manage"
  | "admin.audit.read"
  | "admin.notifications.read"
  | "admin.settings.manage"
  | "session.manage.self";

export type UserRole =
  | "PLANLAMA"
  | "SATIN_ALMA"
  | "SATIN_ALMA_MUDURU"
  | "GENEL_MUDUR"
  | "KALITE_UZMANI"
  | "KALITE_MUDURU"
  | "URETIM_OPERATORU"
  | "VARDIYA_AMIRI"
  | "URETIM_YONETICISI"
  | "TEKNIK_MUDURU"
  | "DEPO_GOREVLISI"
  | "CEP_DEPO_SORUMLUSU"
  | "SISTEM_YONETICISI"
  | "GUEST";

export type SessionUser = {
  id: string;
  email: string;
  name: string;
  initials: string;
  title: string;
  departmentId: string | null;
  department: string;
  assignedRoles: UserRole[];
  roles: UserRole[];
  role: UserRole;
  roleLabel: string;
  permissions: Permission[];
  dataScope: "none" | "own" | "department" | "all" | "assigned";
  preview?: boolean;
  previewRole?: Exclude<UserRole, "SISTEM_YONETICISI" | "GUEST">;
};

export const navPermission: Record<string, Permission> = {
  "Malzeme Planlama": "production.plan.read",
  Talepler: "dashboard.read",
  Teklifler: "offer.read",
  Siparişler: "purchase_order.read",
  "Onay Merkezi": "approval.read.assigned",
  Tedarikçiler: "supplier.read",
  Sözleşmeler: "contract.read",
  Raporlar: "report.read",
  "DÖF Yönetimi": "dof.read",
  "Üretim": "production.entry.read",
  "Üretim Raporları": "production.report.read",
  "İş Emri Takibi": "production.report.read",
  "Depo Hazırlık": "material.preparation.read",
  "Etiket Talepleri": "label.read",
  "Sistem Genel Bakış": "admin.users.manage",
  "Personel ve Erişim Merkezi": "admin.users.manage",
  "Vardiya Formu Yönetimi": "admin.users.manage",
  Kullanıcılar: "admin.users.manage",
  "Roller ve Yetkiler": "admin.users.manage",
  Departmanlar: "admin.departments.manage",
  "Onay Akışları": "admin.workflows.manage",
  "Microsoft 365": "admin.settings.manage",
  "E-posta Bildirimleri": "admin.notifications.read",
  "Aktif Oturumlar": "admin.audit.read",
  "Güvenlik Kayıtları": "admin.audit.read",
  "Sistem Ayarları": "admin.settings.manage",
  Profilim: "session.manage.self",
};

export function can(user: SessionUser, permission: Permission) {
  return user.permissions.includes(permission);
}

const roleNavigation: Record<UserRole, string[]> = {
  PLANLAMA: [
    "Malzeme Planlama",
    "Depo Hazırlık",
    "Talepler",
    "Siparişler",
    "DÖF Yönetimi",
    "Profilim",
  ],
  SATIN_ALMA: [
    "Talepler",
    "Teklifler",
    "Siparişler",
    "Tedarikçiler",
    "Sözleşmeler",
    "DÖF Yönetimi",
    "Profilim",
  ],
  SATIN_ALMA_MUDURU: [
    "Talepler",
    "Siparişler",
    "Tedarikçiler",
    "Sözleşmeler",
    "Raporlar",
    "Profilim",
  ],
  GENEL_MUDUR: ["Talepler", "Sözleşmeler", "Raporlar", "Profilim"],
  KALITE_UZMANI: ["Siparişler", "Tedarikçiler", "Sözleşmeler", "DÖF Yönetimi", "Profilim"],
  KALITE_MUDURU: ["Siparişler", "Tedarikçiler", "Sözleşmeler", "DÖF Yönetimi", "Raporlar", "Profilim"],
  URETIM_OPERATORU: ["Üretim", "Profilim"],
  VARDIYA_AMIRI: ["Üretim", "Depo Hazırlık", "Profilim"],
  URETIM_YONETICISI: ["Üretim", "İş Emri Takibi", "Depo Hazırlık", "Profilim"],
  TEKNIK_MUDURU: ["Üretim", "Profilim"],
  DEPO_GOREVLISI: ["Depo Hazırlık", "Profilim"],
  CEP_DEPO_SORUMLUSU: ["Depo Hazırlık", "Profilim"],
  SISTEM_YONETICISI: [
    "Sistem Genel Bakış",
    "Personel ve Erişim Merkezi",
    "Vardiya Formu Yönetimi",
    "Üretim",
    "Üretim Raporları",
    "İş Emri Takibi",
    "Depo Hazırlık",
    "Malzeme Planlama",
    "Talepler",
    "Teklifler",
    "Onay Merkezi",
    "Siparişler",
    "Tedarikçiler",
    "Sözleşmeler",
    "Raporlar",
    "DÖF Yönetimi",
    "Kullanıcılar",
    "Roller ve Yetkiler",
    "Departmanlar",
    "Onay Akışları",
    "Microsoft 365",
    "E-posta Bildirimleri",
    "Aktif Oturumlar",
    "Güvenlik Kayıtları",
    "Sistem Ayarları",
    "Profilim",
  ],
  GUEST: [],
};

export function roleCanNavigate(user: SessionUser, view: string) {
  if (view === "Etiket Talepleri") return can(user, "label.read");
  return roleNavigation[user.role].includes(view);
}

export function navigationLabel(user: SessionUser, view: string) {
  if (view === "Siparişler" && user.role === "PLANLAMA") {
    return "Sipariş Takibi";
  }
  if (
    view === "Siparişler" &&
    (user.role === "KALITE_UZMANI" || user.role === "KALITE_MUDURU")
  ) {
    return "Kalite Kontrol Kuyruğu";
  }
  if (
    view === "DÖF Yönetimi" &&
    (user.role === "PLANLAMA" || user.role === "SATIN_ALMA")
  ) {
    return "DÖF Görevlerim";
  }
  if (view === "Talepler" && user.role === "GENEL_MUDUR") {
    return "Nihai Onaylar";
  }
  if (view === "Raporlar" && user.role === "GENEL_MUDUR") {
    return "Yönetim Raporları";
  }
  if (view === "Raporlar" && user.role === "KALITE_MUDURU") {
    return "Kalite Raporları";
  }
  return view;
}

export function firstAvailableView(user: SessionUser) {
  if (user.role === "SISTEM_YONETICISI") return "Sistem Genel Bakış";
  if (user.role === "GENEL_MUDUR") return "Talepler";
  if (user.role === "PLANLAMA") return "Talepler";
  if (user.role === "KALITE_UZMANI" || user.role === "KALITE_MUDURU") {
    return "DÖF Yönetimi";
  }
  if (user.role === "DEPO_GOREVLISI" || user.role === "CEP_DEPO_SORUMLUSU") return "Depo Hazırlık";
  if (user.role === "URETIM_OPERATORU" || user.role === "VARDIYA_AMIRI" || user.role === "URETIM_YONETICISI" || user.role === "TEKNIK_MUDURU") {
    return "Üretim";
  }
  return (
    roleNavigation[user.role].find((view) => can(user, navPermission[view])) ??
    "Erişim Yok"
  );
}
